import React, { useState } from 'react';
import Banner from '../common/Banner';
import { useAuth } from '../../context/authcontext';
import { useSubscription } from '../../hooks/useSubscription';
import { useCanPerform } from '../../hooks/useCanPerform';
import { openOrganizationAccount } from '../../constants/navigationEvents';
import { CAP_WARNING_PCT, capPercent, formatCents, planLabel } from '../../utils/subscription';
import type { AccountSubscription } from '../../types/permissions';

/** Phase 21: the included-patients notice starts this many patients before the included count. */
export const INCLUDED_NOTICE_MARGIN = 2;

/** Cap usage when the banner applies (≥80% of a fixed cap), else null. */
export function capBannerUsage(subscription: AccountSubscription | null | undefined): { used: number; limit: number; pct: number } | null {
  if (!subscription) return null;
  const { used, limit } = subscription.cap;
  if (limit === null) return null;
  const pct = capPercent(used, limit);
  if (pct === null || pct < CAP_WARNING_PCT) return null;
  return { used, limit, pct };
}

/**
 * Phase 21: plans without a hard cap (usage pricing) get an info notice once active patients reach
 * the included count minus INCLUDED_NOTICE_MARGIN; null otherwise.
 */
export function includedNoticeUsage(
  subscription: AccountSubscription | null | undefined
): { used: number; included: number; perPatientCents: number } | null {
  if (!subscription) return null;
  const { used, limit, includedPatients, perPatientCents } = subscription.cap;
  if (limit !== null || includedPatients === null || perPatientCents === null || perPatientCents <= 0) return null;
  if (used < includedPatients - INCLUDED_NOTICE_MARGIN) return null;
  return { used, included: includedPatients, perPatientCents };
}

/** localStorage key for the notice dismissal: per user and per billing period. */
export function includedNoticeKey(userId: string, subscription: AccountSubscription): string {
  const period = subscription.periodStart ?? subscription.currentPeriodEnd ?? subscription.trialEndsAt ?? 'current';
  return `pelora.includedPatientsNotice.dismissed:${userId}:${period}`;
}

function readDismissed(key: string): boolean {
  try {
    return window.localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function writeDismissed(key: string): void {
  try {
    window.localStorage.setItem(key, '1');
  } catch {
    // Storage blocked (private window): the notice is dismissed for this page view only
  }
}

interface CapUsageBannerProps {
  className?: string;
}

/**
 * Patient cap notice (Phase 13, ACC-10): "{used} of {limit} patients on {Plan}" once 80% of a hard
 * cap (Starter) is in use; at the cap it turns into a warning. Managers get "View plans".
 * Phase 21: plans without a hard cap show a dismissible info notice near the included count
 * ("23 of 25 included patients; each further patient is $9 a month."). Never blocking.
 */
const CapUsageBanner: React.FC<CapUsageBannerProps> = ({ className = '' }) => {
  const { user } = useAuth();
  const subscription = useSubscription();
  const canManage = useCanPerform('manage:subscription');
  const [dismissedKey, setDismissedKey] = useState<string | null>(null);
  const usage = capBannerUsage(subscription);
  const included = usage ? null : includedNoticeUsage(subscription);
  if (!subscription || (!usage && !included)) return null;

  const viewPlans = canManage ? (
    <button
      type="button"
      onClick={openOrganizationAccount}
      className="rounded border border-current bg-white px-3 py-1.5 text-xs font-medium hover:bg-slate-50"
    >
      View plans
    </button>
  ) : undefined;

  if (included) {
    const key = includedNoticeKey(user?.id ?? 'user', subscription);
    if (dismissedKey === key || readDismissed(key)) return null;
    const rate = formatCents(included.perPatientCents);
    return (
      <Banner
        variant="info"
        data-testid="included-patients-notice"
        className={className}
        action={viewPlans}
        onDismiss={() => {
          writeDismissed(key);
          setDismissedKey(key);
        }}
      >
        {included.used <= included.included
          ? `${included.used} of ${included.included} included patients; each further patient is ${rate} a month.`
          : `${included.used} active patients, ${included.used - included.included} above the ${included.included} included; each is ${rate} a month.`}
        {subscription.status === 'trialing' && ' Nothing is charged for extra patients during your trial.'}
      </Banner>
    );
  }

  if (!usage) return null;
  const atCap = usage.used >= usage.limit;
  return (
    <Banner
      variant={atCap ? 'warning' : 'info'}
      data-testid="cap-usage-banner"
      className={className}
      action={viewPlans}
    >
      {usage.used} of {usage.limit} patients on {planLabel(subscription.plan)}.
      {atCap ? ' Upgrade to admit or resume more patients.' : ' Pending admission, on hold and discharged patients do not count.'}
    </Banner>
  );
};

export default CapUsageBanner;
